// The form fields of a document as the Reader's engines list them, including hidden ones: name, kind, value and where the field's
// dictionary lives. formWidgets() (form.ts) is the on-screen view and leaves hidden widgets out; this is the data view, and it
// follows the rules of rd_pdf::forms (Rust) so a fingerprint made by one verifies in the other.

import { PdfDocument, textString } from "./document";
import { Dict, PName, PRef, type PObj } from "./parse";

export type FieldKindName = "Text" | "Checkbox" | "Radio" | "PushButton" | "Combo" | "List" | "Signature" | "Other";

export interface RawField {
    name: string;
    kind: FieldKindName;
    value: string;
    flags: number;
    /** The object number of the dictionary that holds the field's name (the field), when it is an indirect object. */
    num: number | null;
    dict: Dict;
    /** Object numbers of the widgets (the field itself counts when it is its own widget). */
    widgets: number[];
}

const INHERIT = ["FT", "V", "Ff", "DA", "Opt", "MaxLen"] as const;

export function readFields(doc: PdfDocument): RawField[] {
    const form = doc.catalog.dict("AcroForm");
    const fields = form?.arr("Fields");
    const xref = doc.xref;
    const out: RawField[] = [];
    if (!form || !fields) return out;
    const seen = new Set<Dict>();
    const walk = (node: PObj, prefix: string, inh: Map<string, PObj>, depth: number) => {
        if (depth > 20) return;
        const ref = node instanceof PRef ? node : null;
        const d = xref.resolve(node);
        if (!(d instanceof Dict) || seen.has(d)) return;
        seen.add(d);
        const here = new Map(inh);
        for (const k of INHERIT) { const v = d.raw(k); if (v !== undefined) here.set(k, v); }
        const t = d.get("T");
        const partial = typeof t === "string" ? textString(t) : "";
        const name = !prefix ? partial : !partial ? prefix : `${prefix}.${partial}`;
        const kids = (d.arr("Kids") ?? []).filter((k): k is PRef => k instanceof PRef);
        const kidDicts = kids.map((k) => xref.resolve(k));
        const subFields = kids.filter((_, i) => { const kd = kidDicts[i]; return kd instanceof Dict && kd.has("T"); });
        const widgetKids = kids.filter((_, i) => { const kd = kidDicts[i]; return kd instanceof Dict && !kd.has("T"); });
        for (const k of subFields) walk(k, name, here, depth + 1);
        const ownWidget = d.has("Rect") && kids.length === 0;
        if (!widgetKids.length && !ownWidget) return;
        const res = (k: string): PObj => { const v = here.get(k); return v === undefined ? null : xref.resolve(v); };
        const ff = (() => { const v = res("Ff"); return typeof v === "number" ? v : 0; })();
        const ft = (() => { const v = res("FT"); return v instanceof PName ? v.name : null; })();
        const kind: FieldKindName = ft === "Tx" ? "Text" : ft === "Btn" ? (ff & (1 << 16) ? "PushButton" : ff & (1 << 15) ? "Radio" : "Checkbox") : ft === "Ch" ? (ff & (1 << 17) ? "Combo" : "List") : ft === "Sig" ? "Signature" : "Other";
        const v = res("V");
        const value = typeof v === "string" ? textString(v) : v instanceof PName ? v.name : Array.isArray(v) ? v.map((x) => xref.resolve(x)).filter((x): x is string => typeof x === "string").map(textString).join(", ") : "";
        out.push({ name, kind, value, flags: ff, num: ref ? ref.num : null, dict: d, widgets: ownWidget ? (ref ? [ref.num] : []) : widgetKids.map((k) => k.num) });
    };
    for (const f of fields) walk(f, "", new Map(), 0);
    return out;
}

/** (name, value) of every field that holds something, in document order; buttons, signatures and unknown kinds are left out. */
export function formData(fields: RawField[]): Array<[string, string]> {
    return fields
        .filter((f) => f.kind !== "PushButton" && f.kind !== "Signature" && f.kind !== "Other")
        .map((f): [string, string] => [f.name, f.kind === "Checkbox" || f.kind === "Radio" ? (f.value && f.value !== "Off" ? f.value : "") : f.value]);
}
